import { compact, pick, last } from 'lodash';
import {
  ValidationErrors, ValidationError, CompetitionScoreLevel, UserRead, Comment,
  Story, UserWrite, CompetitionPledge, MembershipLevel, UserType, PledgeCompletions, CompetitionUserScore, CompetitionUserScores, CompetitionScoreLevels, CompetitionPledgeUserType, Competition, RGBA,
  ImageMutation as ServerImageMutation,
  Entity
} from '../userApi';
import { TranslationFunction } from 'i18next';
import { joinAnd } from './joinAnd';
import { I18next } from './i18nextShim';
import moment = require('moment');
import { ImageMutation } from './image';
import logger from './logger';

export enum CompetitionPledges {
  HighlightAHero = 'd38c64a1-e216-4f91-b66f-e17c50c9e25a',
  InviteAFriend = '34b4e1c4-5217-426e-a966-4c1ff0ea2d71',
  AddAnAction = '71b4fb8d-f5e2-4bc8-8aef-dfac92d8baae',
  AttendAnEvent = '41dcbd5c-90fd-49a6-8f46-fa9ab0039156',
  TakeCalculator = 'cdcb0d93-53af-4c7c-8750-27f389fb8e92',
  TakeSurvey = 'e13cd5b6-351a-4c48-83d9-051428e4a626'
}

/** CCSF end-of-challenge Microsoft Forms survey (500 pts). */
export const CCSFSurveyCloseOutPledgeUUID = '8f3c2a19-6d4e-4b8a-9f1c-2e5d7b94a601';

const CLOSEOUT_SURVEY_TITLE_RE = /survey close[- ]?out/i;

/** End-of-challenge surveys: always available (even before Introduce Yourself). */
export const isCloseOutSurveyPledge = (pledge?: CompetitionPledge | null): boolean => {
  if (!pledge) return false;
  if (pledge.uuid === CompetitionPledges.TakeSurvey) return true;
  if (pledge.uuid === CCSFSurveyCloseOutPledgeUUID) return true;
  return CLOSEOUT_SURVEY_TITLE_RE.test(pledge.shortTitle || '');
};

const LEGACY_CLOSEOUT_STOCK_PHOTO_RE = /Economy%20\(59\)|Economy \(59\)/i;

/** Pledges where "I already do / new to me" is not a meaningful habit signal. */
const PLEDGES_WITHOUT_ALREADY_DO_STATUS: { [uuid: string]: true } = {
  [CompetitionPledges.HighlightAHero]: true,
  [CompetitionPledges.InviteAFriend]: true,
  [CompetitionPledges.TakeCalculator]: true,
  [CompetitionPledges.TakeSurvey]: true,
  [CompetitionPledges.AddAnAction]: true,
  [CompetitionPledges.AttendAnEvent]: true,
  [CCSFSurveyCloseOutPledgeUUID]: true,
  // Introduce yourself! (CCSF / campus welcome story)
  'd58269d1-2f5c-4b81-a3f5-6689d347ce46': true
};

/** Available alongside (and ahead of) the calculator in onboarding. */
export const IntroduceYourselfPledgeUUID = 'd58269d1-2f5c-4b81-a3f5-6689d347ce46';

/**
 * One-shot social / media / learning actions where habit status and home/work
 * location are not meaningful (even if qualify_location is true in the DB).
 */
const NO_STATUS_OR_LOCATION_TITLE_RE = new RegExp([
  'introduce yourself',
  'invite a friend',
  'highlight a hero',
  '^watch\\b',
  '\\bvideo\\b',
  'carbon calculator',
  'take the calculator',
  'take calculator',
  'take a survey',
  'add an action',
  'attend an event',
  'go to an event'
].join('|'), 'i');

/**
 * "I already do / new to me" is for personal habit-change actions.
 * Hide for tagging/nomination pledges, known one-shot system pledges, and
 * any pledge that already uses user tagging (canTagUsers).
 */
export const showsAlreadyDoStatusField = (pledge?: CompetitionPledge | null): boolean => {
  if (!pledge) return false;
  if (pledge.canTagUsers) return false;
  if (PLEDGES_WITHOUT_ALREADY_DO_STATUS[pledge.uuid]) return false;
  const title = pledge.shortTitle || '';
  if (NO_STATUS_OR_LOCATION_TITLE_RE.test(title)) return false;
  return true;
};

/**
 * Home/work location only for habit-style actions that opt in via qualifyLocation.
 * Introduce yourself, invite a friend, watch a video, etc. never show it.
 */
export const showsLocationQualifierField = (pledge?: CompetitionPledge | null): boolean => {
  if (!pledge || !pledge.qualifyLocation) return false;
  return showsAlreadyDoStatusField(pledge);
};

export type AlreadyDoStatusTone = 'habit' | 'completed';

/**
 * Habit actions use present tense ("I already do this").
 * One-time installs/decisions/signups/learning use past tense ("I've already done this").
 * Default is habit — CCSF catalogs many ongoing practices with repeats=1.
 */
const ONE_TIME_COMPLETION_TITLE_RE = new RegExp([
  '^sign up\\b',
  '\\bchange your banking\\b',
  '^divest\\b',
  '\\binstall\\b',
  '^convert to\\b',
  '^check your home\\b',
  '^familiarize\\b',
  '^educate yourself\\b',
  '^research\\b',
  '^watch\\b',
  '^read a book\\b',
  '^read sf\\b',
  '^locate\\b',
  '^find your birthday\\b',
  '^view the\\b',
  '^attend\\b',
  '^go to an event\\b',
  '^organize\\b',
  '^meet your neighbors\\b',
  '^ask a colleague\\b',
  '^follow a podcast\\b',
  '^learn how to\\b',
  '\\blibrary card\\b',
  '\\bwater-wise evaluation\\b',
  '\\blow-flow showerhead\\b'
].join('|'), 'i');

export const alreadyDoStatusTone = (pledge?: CompetitionPledge | null): AlreadyDoStatusTone => {
  if (!pledge) return 'habit';
  const title = pledge.shortTitle || '';
  if (ONE_TIME_COMPLETION_TITLE_RE.test(title)) return 'completed';
  return 'habit';
};

/** Dropdown labels for the already-do / new status field. */
export const alreadyDoStatusLabels = (
  pledge: CompetitionPledge | null | undefined,
  t: (key: string) => string
): string[] => {
  if (alreadyDoStatusTone(pledge) === 'completed') {
    return [t('----'), t('I\'ve already done this'), t('This is new to me')];
  }
  return [t('----'), t('I already do this'), t('This is new to me')];
};

// export const competitionName = 'coolcampuschallenge2019';

export const isValidationErrors = (err: any): err is ValidationErrors => {
  return err instanceof Array &&
    (err as ValidationError[]).some((validationError) => validationError.code !== undefined);
};

export const isResponse = (err: any): err is Response =>
  err.json instanceof Function && err.text instanceof Function;

/** Like/flag create often return 201 or 204 with an empty body. */
export const isSuccessfulResponse = (err: unknown): err is Response =>
  isResponse(err) && err.status >= 200 && err.status < 300;

export const getValidationErrors = async (err: any) => {
  if (!isResponse(err)) {
    return;
  }
  const res = err.clone();
  try {
    const payload = await res.json();
    if (isValidationErrors(payload)) {
      return payload;
    }
  } catch (readErr) {
    // Not validation error response. Pass.
  }
  return;
};

export const getScoreLevelColor = (scoreLevel: CompetitionScoreLevel) => {
  switch (scoreLevel.name) {
    case 'Champion':
      return '#e44c9a';
    case 'Ambassador':
      return '#ff8f28';
    case 'Apprentice':
      return '#ffd200';
    case 'Novice':
      return '#47A4D6';
    default:
      return '#808080';
  }
};

export const getScoreLevelPhoto = (scoreLevel: CompetitionScoreLevel, size: '' | '@2x' | '@3x' = '') => {
  switch (scoreLevel.name) {
    case 'Champion':
      return `/assets/score_levels/champion-icon-large${size}.png`;
    case 'Ambassador':
    case 'Advocate':
    case 'Activist':
      return `/assets/score_levels/ambassador-icon-large${size}.png`;
    case 'Apprentice':
      return `/assets/score_levels/apprentice-icon-large${size}.png`;
    case 'Novice':
      return `/assets/score_levels/novice-icon-large${size}.png`;
    default:
      return null;
  }
};

export const categoryPhoto: Record<string, string> = {
  // Match against competition pledge category names (substring, case-insensitive).
  community: '/assets/pledges/community.png',
  transportation: '/assets/pledges/transportation.png',
  food: '/assets/pledges/food.png',
  energy: '/assets/pledges/energy.png',
  reuse: '/assets/pledges/reuse.png',
  refuse: '/assets/pledges/reuse.png',
  water: '/assets/pledges/water.png',
  justice: '/assets/pledges/justice.png',
  education: '/assets/pledges/community.png',
  learning: '/assets/pledges/community.png',
  economy: '/assets/pledges/community.png',
  toxics: '/assets/pledges/community.png'
};

export const getCategoryPlaceholderPhoto = (pledgeCategory: string = ''): string => {
  const name = (pledgeCategory || '').toLowerCase();
  for (const category of Object.keys(categoryPhoto)) {
    if (name.indexOf(category.toLowerCase()) !== -1) {
      return categoryPhoto[category];
    }
  }
  return '/assets/pledges/community.png';
};

export const getPledgePhoto = (pledge: CompetitionPledge, pledgeCategory: string): string | undefined => {
  if (pledge.photo && !LEGACY_CLOSEOUT_STOCK_PHOTO_RE.test(pledge.photo)) {
    return pledge.photo;
  }
  if (isCloseOutSurveyPledge(pledge) && pledge.photo) {
    return undefined;
  }
  if (pledge.photo) {
    return getCategoryPlaceholderPhoto(pledgeCategory);
  }
  if (isCloseOutSurveyPledge(pledge)) {
    return undefined;
  }
  return getCategoryPlaceholderPhoto(pledgeCategory);
};

/** Swap a broken <img> to the category placeholder (once). */
export const fallbackPledgePhotoOnError = (
  event: { currentTarget: HTMLImageElement },
  pledgeCategory: string = ''
) => {
  const img = event.currentTarget;
  const fallback = getCategoryPlaceholderPhoto(pledgeCategory);
  if (!fallback || img.dataset.fallbackApplied === '1') {
    img.onerror = null;
    return;
  }
  img.dataset.fallbackApplied = '1';
  img.src = fallback;
};

export const displayFullName = (user: UserRead) =>
  compact([user.firstName, user.lastName]).join(' ');

export const commentableTypeToString = (commentableType: Comment.CommentableTypeEnum) => {
  switch (commentableType) {
    case Comment.CommentableTypeEnum.Story:
      return 'story';
    case Comment.CommentableTypeEnum.BlogEntry:
      return 'blog_entry';
    default:
      throw new Error(`Unsupported commentableType: ${commentableType}.`);
  }
};

const { LocationQualifierEnum } = Story;

export const toLocationQualifierEnum = (value: string): Story.LocationQualifierEnum => {
  switch (value) {
    case 'home':
      return LocationQualifierEnum.Home;
    case 'work':
      return LocationQualifierEnum.Work;
    case 'both':
      return LocationQualifierEnum.Both;
    default:
      if (value !== '') logger.error(new Error(`Unsupported Story.LocationQualifierEnum ${value}.`));
      return LocationQualifierEnum.Empty;
  }
};
const { StatusEnum } = Story;

export const toStoryStatusEnum = (value: string): Story.StatusEnum => {
  switch (value) {
    case 'already_do':
      return StatusEnum.AlreadyDo;
    case 'new':
      return StatusEnum.New;
    default:
      if (value !== '') logger.error(new Error(`Unsupported Story.StatusEnum ${value}.`));
      return StatusEnum.Empty;
  }
};

// Autogenerated code does not support multi-part form data.
// It is fixed in a future release: https://github.com/OpenAPITools/openapi-generator/pull/569
export const postFormData = async <T>(url: string, body: FormData, token?: string): Promise<T> => {
  const options = {
    body,
    headers: { Authorization: token },
    method: 'POST'
  };
  const response = await fetch(url, options);
  if (response.status >= 200 && response.status < 300) {
    return response.json();
  } else {
    throw response;
  }
};

export const userReadToWrite = (user: UserRead): UserWrite =>
  pick(user, [
    'type', 'email', 'firstName', 'lastName', 'profilePhoto', 'descriptionMD',
    'descriptionHTML', 'public', 'socialLinks', 'options'
  ]) as UserWrite;

export const makeCampusHeroTitle = (taggedUsers: UserRead[], t: TranslationFunction) => {
  let title = '';
  if (taggedUsers.length > 1) {
    title = t('Cool Campus Heroes', { ns: 'story' });
  } else {
    title = t('Cool Campus Hero', { ns: 'story' });
  }
  const userNames = taggedUsers.map((user) => displayFullName(user));
  return `${title}: ${joinAnd(userNames, t)}`;
};

export const makeStoryTitle = (story: Story, pledge: CompetitionPledge, taggedUsers: UserRead[], t: TranslationFunction) => {
  if (!pledge) return '';
  if (pledge.uuid === CompetitionPledges.HighlightAHero) {
    return makeCampusHeroTitle(taggedUsers, t);
  }
  return pledge.shortTitle;
};

export const atLeastAdmin = (level: MembershipLevel) =>
  (level === MembershipLevel.Admin || level === MembershipLevel.Superadmin);

export const atLeastSuperadmin = (level: MembershipLevel) =>
  (level === MembershipLevel.Superadmin);

export const displayUserType = (userType: UserType, i18n: I18next) => {
  const t = i18n.getFixedT(i18n.language, 'userType');
  switch (userType) {
    case UserType.Student:
      return t('Student');
    case UserType.Faculty:
      return t('Faculty');
    case UserType.Staff:
      return t('Staff');
    default:
      return '';
  }
};

export const displayCompetitionPledgeUserType = (userType: CompetitionPledgeUserType, i18n: I18next) => {
  const t = i18n.getFixedT(i18n.language, 'userType');
  switch (userType) {
    case CompetitionPledgeUserType.Staff:
      return t('staff');
    case CompetitionPledgeUserType.Labs:
      return t('labs');
    default:
      logger.error(new Error(`Unexpect CompetitionPledgeUserType ${userType}.`));
      return '';
  }
};

export const userTypeFromString = (userTypeS: string): UserType =>
  UserType[Object.keys(UserType).find((userType) => UserType[userType as any].toString() === userTypeS) as any] as any;

export const pointsPerPhoto = 50;
export const pointsPerStoryRepeat = 50;

export const pledgeCompletionCount = (
  pledgeCompletions: PledgeCompletions | undefined,
  pledge: CompetitionPledge | null | undefined
): number => {
  if (!pledge) return 0;
  return (pledgeCompletions && pledgeCompletions[pledge.id]) || 0;
};

export const repeatPointsForPledge = (pledge: CompetitionPledge): number =>
  Math.min(pledge.points, pointsPerStoryRepeat);

export const calculatePointsForNextStory = (pledgeCompletions: PledgeCompletions, pledge: CompetitionPledge) => {
  if (!pledge) return 0;
  const completions = pledgeCompletionCount(pledgeCompletions, pledge);
  if (completions === 0) return pledge.points;
  if (completions >= pledge.repeats) return 0;
  return repeatPointsForPledge(pledge);
};

/** User-facing copy for the story submit form (Highlight a Hero: 200 then 50). */
export const nextStoryPointsMessage = (
  pledge: CompetitionPledge,
  pledgeCompletions: PledgeCompletions | undefined,
  t: TranslationFunction
): string => {
  const completions = pledgeCompletionCount(pledgeCompletions, pledge);
  const nextPoints = calculatePointsForNextStory(pledgeCompletions || {}, pledge);
  if (nextPoints <= 0) return '';
  if (pledge.repeats > 1 && completions === 0) {
    return t(
      '{{first}} points for your first story, {{repeat}} points for each additional (up to {{max}} stories).',
      { first: pledge.points, repeat: repeatPointsForPledge(pledge), max: pledge.repeats }
    );
  }
  if (pledge.repeats > 1 && completions > 0) {
    return t(
      '{{points}} points for your next story ({{done}} of {{max}} completed).',
      { points: nextPoints, done: completions, max: pledge.repeats }
    );
  }
  return t('{{points}} points for next story submitted', { points: nextPoints });
};

export const estimatePointsAvailable = (pledgeCompletions: PledgeCompletions, pledge: CompetitionPledge) => {
  if (!pledge || !pledgeCompletions) return 0;
  const repeatPoints = Math.min(pledge.points, pointsPerStoryRepeat);
  const totalPointsAvailable = pledge.points + repeatPoints * Math.max(0, pledge.repeats - 1);
  const completions = pledgeCompletions[pledge.id];
  if (completions >= pledge.repeats) return 0;
  if (completions === undefined || completions === 0) return totalPointsAvailable;
  if (completions === 1) return totalPointsAvailable - pledge.points;
  return totalPointsAvailable - repeatPoints * (completions - 1);
};

export const someMatchingEmailDomain = (email: string, permittedDomains: string[]) =>
  permittedDomains.some((domain) => last(email.split('@')).search(domain) >= 0);

export const getCompetitionUserScore = (
  scores: CompetitionUserScores | undefined,
  userID?: number | string | null
): CompetitionUserScore | undefined => {
  if (!scores || userID == null || userID === '') {
    return undefined;
  }
  return scores[userID as any] || scores[String(userID)];
};

// IMPORTANT: These must coincide with validations in app/operations/validate_pledge_eligibility.go.
export const isUserScoreEligibleForPledge = (
  score: CompetitionUserScore | undefined,
  scoreLevels: CompetitionScoreLevels,
  pledge: CompetitionPledge
) => {
  if (!pledge || !pledge.scoreLevelUUID || !scoreLevels) return true;
  const thresholdLevel = scoreLevels.find((scoreLevel) => scoreLevel.uuid === pledge.scoreLevelUUID);
  if (!thresholdLevel) return true;
  const points = score ? score.points : 0;
  return points >= thresholdLevel.threshold;
};

export const isUserTypeEligibleForPledge = (user: UserRead, pledge: CompetitionPledge) => {
  if (!pledge.userTypes || pledge.userTypes.length === 0) return true;
  if (user.options.isLabUser && pledge.userTypes.some((userType) => userType === CompetitionPledgeUserType.Labs)) {
    return true;
  } else if (user.options.isHealthFacilityEmployee && pledge.userTypes.some((userType) => userType === CompetitionPledgeUserType.Staff)) {
    return true;
  }
  return false;
};

export const isDateEligibleForPledge = (pledge: CompetitionPledge, competition: Competition) => {
  if (!pledge.releaseWeek || pledge.releaseWeek === 1) return true;
  return moment(competition.startsAt).add(pledge.releaseWeek - 1, 'weeks').isBefore(Date.now());
};

export const savingsCO2LbsToCars = (savingsCO2Lbs: number): number => savingsCO2Lbs * 96.3 / Math.pow(10, 6);

export const maxImageSizeBytes = 1 * Math.pow(10, 6);
/** Stay under the API /image_mutation multipart limit with margin (legacy limit was 10MB). */
export const maxImageMutationUploadBytes = 28 * 1024 * 1024;
export const maxVideoSizeBytes = 50 * 1024 * 1024;

export const isVideoUrl = (url: string) =>
  /\.(mp4|mov|avi|mpg|mpeg|webm|m4v)(\?|$)/i.test(url);

const hexToRgba = (hex: string): RGBA => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
    a: 1
  } : { r: 0, g: 0, b: 0, a: 0 };
};

export const roundTo = (value: number, decimals: number) => {

  const round = Math.round(value);
  return round;
};

export const imageMutationToServerIM = (mutation: Partial<ImageMutation>, maxMB: number, contentType: ServerImageMutation.ContentTypeEnum): ServerImageMutation => {
  const crop = {
    x: roundTo((mutation.pixelCrop && mutation.pixelCrop.x) || 0, 3),
    y: roundTo((mutation.pixelCrop && mutation.pixelCrop.y) || 0, 3),
    width: (mutation.pixelCrop && mutation.pixelCrop.width) || 0,
    height: (mutation.pixelCrop && mutation.pixelCrop.height) || 0
  };
  if (mutation.image) {
    const imageWidth = mutation.image.clientWidth || mutation.image.width;
    const imageHeight = mutation.image.clientHeight || mutation.image.height;
    const imageFullWidth = mutation.image.naturalWidth;
    const imageFullHeight = mutation.image.naturalHeight;
    crop.x = Math.round(crop.x * imageFullWidth / imageWidth);
    crop.y = Math.round(crop.y * imageFullHeight / imageHeight);
    crop.width = Math.round(crop.width * imageFullWidth / imageWidth);
    crop.height = Math.round(crop.height * imageFullHeight / imageHeight);
  }

  return {
    rotation: -mutation.rotation || 0,
    bgColor: hexToRgba(mutation.backgroundColor),
    crop,
    contentType,
    maxMB
  };
};

export const entityCanBlog = (entity: Entity) => entity.type === Entity.TypeEnum.University;
